import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  acceptSuggestedCampaign,
  dismissSuggestedCampaign,
  fetchSuggestedCampaigns,
  generateCustomCampaign,
  refreshSuggestedCampaigns,
} from '../api';

export function useSuggestedCampaigns() {
  return useGrowthQuery({
    queryKey: growthKeys.suggestedCampaigns(),
    queryFn: fetchSuggestedCampaigns,
  });
}

export function useSuggestedCampaignMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: growthKeys.suggestedCampaigns() });

  const dismiss = useMutation({
    mutationFn: dismissSuggestedCampaign,
    onSuccess: () => {
      notify.success('Suggestion dismissed');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const accept = useMutation({
    mutationFn: acceptSuggestedCampaign,
    onSuccess: () => {
      notify.success('Suggestion accepted — launch pending');
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: [...growthKeys.all, 'campaigns'] });
    },
    onError: (e) => notify.error(e),
  });

  const refresh = useMutation({
    mutationFn: refreshSuggestedCampaigns,
    onSuccess: () => {
      notify.success('Refreshing suggestions');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  const generate = useMutation({
    mutationFn: generateCustomCampaign,
    onSuccess: () => {
      notify.success('Campaign generation queued');
      void invalidate();
    },
    onError: (e) => notify.error(e),
  });

  return { dismiss, accept, refresh, generate };
}
